import { COPY } from '@/shared/constants/copies';
import { PlanType } from '@/shared/services/payment/payment.types';

/**
 * Per-plan limits shown in the UI. Sourced from the pricing doc, 2026-09-19.
 * `null` means unlimited.
 */

/** Plan tiers as used for limit lookups (billing cycle collapsed away). */
export type PlanTier = 'free' | 'starter' | 'business' | 'pro' | 'custom';

/** `null` means unlimited — render as "Unlimited", never as a number. */
export type PlanLimit = number | null;

export const PLAN_TYPE_TO_TIER: Record<PlanType, PlanTier> = {
	[PlanType.FREE]: 'free',
	[PlanType.PRO_MONTHLY]: 'starter',
	[PlanType.PRO_YEARLY]: 'starter',
	[PlanType.UNLIMITED_PRO_MONTHLY]: 'business',
	[PlanType.UNLIMITED_PRO_YEARLY]: 'business',
	[PlanType.PREMIUM_PRO_MONTHLY]: 'pro',
	[PlanType.PREMIUM_PRO_YEARLY]: 'pro',
};

export const ASSIGNMENT_LIMITS: Record<PlanTier, PlanLimit> = {
	free: 100,
	starter: 1000,
	business: null,
	pro: null,
	custom: null,
};

/** Monthly offer letters each plan may send; `null` is unlimited. */
export const OFFER_LETTER_LIMITS: Record<PlanTier, PlanLimit> = {
	free: 1,
	starter: 3,
	business: 50,
	pro: null,
	custom: null,
};

/** Monthly credits included with each plan, in dollars. */
export const PLAN_CREDITS: Record<PlanTier, number> = {
	free: 1,
	starter: 10,
	business: 25,
	pro: 50,
	custom: 0,
};

/** Ordering for "at least this tier" checks. `custom` is negotiated, so it ranks top. */
const PLAN_TIER_RANK: Record<PlanTier, number> = {
	free: 0,
	starter: 1,
	business: 2,
	pro: 3,
	custom: 4,
};

export function isAtLeastTier(planType: PlanType | null | undefined, tier: PlanTier): boolean {
	return PLAN_TIER_RANK[getPlanTier(planType)] >= PLAN_TIER_RANK[tier];
}

export function getPlanRank(planType: PlanType | null | undefined): number {
	return PLAN_TIER_RANK[getPlanTier(planType)];
}

export function formatPlanLimit(limit: PlanLimit): string {
	return limit === null ? COPY.pricing.compareUnlimited : limit.toLocaleString();
}

/** Defaults to Free when the plan type is unknown, so gates fail closed. */
export function getPlanTier(planType?: PlanType | null): PlanTier {
	if (!planType) {
		return 'free';
	}

	return PLAN_TYPE_TO_TIER[planType] ?? 'free';
}
